import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Github, Eye, Code, Database, Globe } from 'lucide-react';

const Projects: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'Personal Portfolio - Tanvi Sharma',
      description: 'A modern, responsive portfolio website showcasing my skills, experience, and projects with beautiful animations and interactive design.',
      category: 'frontend',
      technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Framer Motion', 'Material UI', 'Responsive Design'],
      liveUrl: 'https://portfolio-brown-kappa-10.vercel.app/',
      githubUrl: 'https://github.com/tanvisharma22/portfolio',
      features: ['Responsive Design', 'Smooth Animations', 'Interactive UI', 'Modern Design', 'SEO Optimized']
    },
    {
      id: 2,
      title: 'Distributed Task System',
      description: 'Designed a distributed asynchronous task execution platform with dependency-aware scheduling, priority handling, fault tolerance, and real-time observability.',
      category: 'fullstack',
      technologies: [
        'FastAPI',
        'Celery',
        'Redis',
        'PostgreSQL',
        'React',
        'Docker',
        'JWT',
        'Role-based Access Control'
      ],
      liveUrl: 'https://distributed-task-system.vercel.app/', // optional: keep empty if not deployed publicly
      githubUrl: 'https://github.com/tanvisharma22/distributed-task-system',
      features: [
        'Asynchronous Task Execution',
        'Dependency Graph-Based Scheduling',
        'Priority Scheduling with Aging',
        'Retry Logic with Exponential Backoff',
        'Dead Letter Queue (DLQ)',
        'Real-time Task Monitoring Dashboard',
        'Role-based Access Control',
        'Fault-Tolerant Worker Execution'
      ]
    },
    {
      id: 3,
      title: 'Mini Helpdesk System (AI-Powered)',
      description: 'Built a full-stack helpdesk platform with AI-powered ticket summarization and automated reply generation to improve support efficiency and response quality.',
      category: 'fullstack',
      technologies: [
        'React',
        'TypeScript',
        'Node.js',
        'Express',
        'MongoDB',
        'JWT',
        'Role-Based Access Control',
        'LLM API Integration',
        'Prompt Engineering',
        'Vercel'
      ],
      liveUrl: 'https://helpdesk-two-sooty.vercel.app/public/submit',
      githubUrl: 'https://github.com/tanvisharma22/Helpdesk', // replace if different
      features: [
        'Public Ticket Submission & Status Tracking',
        'JWT-based Agent Authentication',
        'Role-Based Access Control (Agent/Admin)',
        'Ticket Lifecycle Management with Timeline',
        'Server-side Search, Filtering & Pagination',
        'AI-based Ticket Summarization',
        'AI-generated Draft Replies for Agents',
        'Prompt Engineering for Context-Aware Responses',
        'Secure API Design with Validation & Rate Limiting',
        'Responsive UI with Optimistic Updates'
      ]
    },
    
    {
      id: 4,
      title: 'Recommendation System Platform',
      description: 'Architected an asynchronous recommendation engine leveraging collaborative filtering, hybrid ranking, and diversity-aware re-ranking for personalized content discovery.',
      category: 'backend',
      technologies: [
        'FastAPI',
        'Python',
        'Motor (Async MongoDB)',
        'NumPy',
        'Pandas',
        'scikit-learn'
      ],
      liveUrl: 'https://recommendation-system-8gwz.onrender.com/docs', // optional: leave empty if not publicly deployed
      githubUrl: 'https://github.com/tanvisharma22/Recommendation_system',
      features: [
        'Asynchronous Inference with Non-blocking I/O',
        'User-based Collaborative Filtering',
        'Item-based Collaborative Filtering',
        'Hybrid Recommendation Strategy',
        'MMR Re-ranking for Diversity',
        'Real-time Evaluation Metrics (Precision, Recall, MAP, NDCG)'
      ]
    },
    
    // {
    //   id: 4,
    //   title: 'College Dwarka - Student Portal',
    //   description: 'A comprehensive student management system with dashboards, job portals, placement tracking, and real-time chat functionality.',
    //   category: 'fullstack',
    //   technologies: ['React', 'Node.js', 'MySQL', 'Socket.io', 'REST APIs'],
    //   liveUrl: 'https://collegedwarka.com',
    //   githubUrl: '#',
    //   features: ['Student Dashboards', 'Job Portal', 'Placement Tracking', 'Real-time Chat System']
    // },
    // {
    //   id: 5,
    //   title: 'TravelHub - Travel Booking Platform',
    //   description: 'A comprehensive travel booking platform offering car rentals, tour packages, and travel services with seamless booking experience.',
    //   category: 'fullstack',
    //   technologies: ['Html', 'PHP', 'SEO Optimization'],
    //   liveUrl: 'https://travelhub.tours',
    //   githubUrl: '#',
    //   features: ['Car Rentals', 'Tour Packages', 'Online Booking', 'Responsive Design']
    // },
    {
      id: 6,
      title: 'Zora Travels - Taxi Services',
      description: 'A taxi booking platform specializing in outstation travel with comprehensive vehicle fleet and tour packages.',
      category: 'fullstack',
      technologies: ['Html', 'PHP', 'SEO Optimization'],
      liveUrl: 'https://www.zoratravels.com',
      githubUrl: '#',
      features: ['Taxi Booking', 'Outstation Travel', 'Vehicle Fleet', 'Tour Packages']
    },
    {
      id: 7,
      title: 'GM Virk Travels - Travel Agency',
      description: 'A travel agency website offering comprehensive travel services including tours, transportation, and travel planning.',
      category: 'fullstack',
      technologies: ['Html', 'PHP', 'SEO Optimization'],
      liveUrl: 'https://www.gmvirktravels.com',
      githubUrl: '#',
      features: ['Travel Services', 'Tour Planning', 'Transportation', 'Customer Support']
    }
  ];
  

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'frontend', label: 'Frontend' },
    { id: 'backend', label: 'Backend' },
    { id: 'fullstack', label: 'Full Stack' }
  ];

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'frontend':
        return <Code className="w-4 h-4" />;
      case 'backend':
        return <Database className="w-4 h-4" />;
      case 'fullstack':
        return <Globe className="w-4 h-4" />;
      default:
        return <Code className="w-4 h-4" />;
    }
  };

  return (
    <section id="projects" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A showcase of my recent work, demonstrating my skills in full-stack development and problem-solving
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto rounded-full mt-6"></div>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${activeFilter === filter.id
                  ? 'bg-primary-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-primary-50 hover:text-primary-600 border border-gray-200'
                }`}
            >
              {filter.label}
            </button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg card-hover group flex flex-col h-full"
            >
              {/* Project Header */}
              <div className="relative p-6 bg-gradient-to-r from-primary-50 to-purple-50 border-b border-gray-200">
                {/* Category Badge */}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2 text-sm font-medium shadow-sm">
                  {getCategoryIcon(project.category)}
                  {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                </div>

                {/* Action Buttons */}
                <div className="absolute top-4 right-4 flex gap-2">
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-primary-100 transition-colors shadow-sm"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-700" />
                  </a>
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-primary-100 transition-colors shadow-sm"
                  >
                    <Github className="w-4 h-4 text-gray-700" />
                  </a>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6 flex flex-col h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {project.title}
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">Technologies Used:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech) => (
                      <span key={tech} className="skill-badge text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">Key Features:</h4>
                  <div className="flex flex-wrap gap-1">
                    {project.features.map((feature, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Project Links */}
                <div className="flex gap-3 mt-auto">
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-center py-3 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <Eye className="w-4 h-4" />
                    Live Demo
                  </a>
                  {/* <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 border-2 border-gray-300 hover:border-primary-600 text-gray-700 hover:text-primary-600 text-center py-3 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 hover:bg-primary-50"
                  >
                    <Github className="w-4 h-4" />
                    Code
                  </a> */}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-2xl p-8 border border-primary-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Interested in Working Together?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              I'm always open to discussing new opportunities and exciting projects.
              Let's create something amazing together!
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl"
            >
              Get In Touch
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 